import React, { useCallback, useEffect, useRef, useState } from 'react';
import { node, string } from 'prop-types';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';

import { IconChevronLeft, IconChevronRight } from './Icons';
import css from './ScrollRow.module.css';

// Sub-pixel rounding means scrollLeft rarely lands exactly on 0 or on the
// maximum, so treat anything within a couple of pixels as "at the edge".
const EDGE_TOLERANCE = 2;

// How much of the visible row a single arrow click advances. Keeping it just
// under a full page leaves one tile of overlap, so the user never loses their
// place while paging through a long row.
const PAGE_FRACTION = 0.85;

/**
 * A single, horizontally scrollable row of tiles with prev/next arrow buttons.
 *
 * Homepage product sections always show exactly one row, however many tiles
 * they contain — the overflow is reached by scrolling rather than by the
 * section growing taller. The row is a native scroll container, so touch
 * swiping and trackpad scrolling work for free; the arrows are an additional
 * affordance for mouse users and are disabled (and visually hidden) when there
 * is nothing more to scroll to in that direction.
 *
 * @param {Object} props
 * @param {ReactNode} props.children one element per tile
 * @param {string} [props.className] extra class for the wrapper
 * @param {string} [props.itemClassName] extra class for each tile wrapper
 * @param {string} [props.label] accessible name for the scrollable region
 */
const ScrollRow = props => {
  const { children, className, itemClassName, label } = props;
  const intl = useIntl();
  const scrollerRef = useRef(null);
  const [scrollState, setScrollState] = useState({ canScrollPrev: false, canScrollNext: false });
  const { canScrollPrev, canScrollNext } = scrollState;

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setScrollState({
      canScrollPrev: el.scrollLeft > EDGE_TOLERANCE,
      canScrollNext: el.scrollLeft < maxScrollLeft - EDGE_TOLERANCE,
    });
  }, []);

  // Re-measure on mount, whenever the tiles change, and whenever the row is
  // resized (viewport changes, images loading in, etc.). ResizeObserver is
  // missing in jsdom, so the observer is optional rather than assumed.
  useEffect(() => {
    updateScrollState();

    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children, updateScrollState]);

  const scrollByPage = direction => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    const amount = el.clientWidth * PAGE_FRACTION * direction;
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left: amount, behavior: 'smooth' });
    } else {
      el.scrollLeft += amount;
    }
  };

  const arrowButton = (direction, canScroll, Icon, messageId) => (
    <button
      type="button"
      className={classNames(css.arrow, {
        [css.arrowPrev]: direction < 0,
        [css.arrowNext]: direction > 0,
        [css.arrowHidden]: !canScroll,
      })}
      onClick={() => scrollByPage(direction)}
      disabled={!canScroll}
      aria-label={intl.formatMessage({ id: messageId })}
    >
      <Icon className={css.arrowIcon} />
    </button>
  );

  return (
    <div className={classNames(css.root, className)}>
      <div
        className={css.scroller}
        ref={scrollerRef}
        onScroll={updateScrollState}
        // A scrollable region needs to be keyboard reachable to be operable.
        tabIndex={0}
        role="group"
        aria-label={label}
      >
        {React.Children.map(children, child =>
          child == null ? null : <div className={classNames(css.item, itemClassName)}>{child}</div>
        )}
      </div>

      {arrowButton(-1, canScrollPrev, IconChevronLeft, 'LandingPage.scrollRow.previous')}
      {arrowButton(1, canScrollNext, IconChevronRight, 'LandingPage.scrollRow.next')}
    </div>
  );
};

ScrollRow.propTypes = {
  children: node,
  className: string,
  itemClassName: string,
  label: string,
};

export default ScrollRow;
